import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import ApiReducer from "@common/ApiSlice";
import BackgroundReportReducer from "@common/BackgroundReportSlice";
import ChemicalAdjustmentReducer from "@common/ChemicalAdjustmentSlice";
import GlobalDataReducer from "@common/GlobalDataSlice";
import MldReportReducer from "@common/MldReportSlice";
import ProjectInfoReducer from "@common/ProjectInfoSlice";
import SystemDesignSliceReducer from "@common/SystemDesignSlice";
import UserInfoReducer from "@common/UserInfoSlice";
import GUnitConversionReducer from "@common/utils/GlobalUnitConversionSlice";
import WaterTypesReducer from "@common/WaterTypesSlice";

import { postApi } from "@services/apiConfig";

import FeedSetupSlice from "@features/FeedSetup/store";
import activityMonitorSlice from "@features/feedwater/activitymonitor/activityMonitorSlice";
import IXStoreReducer from "@features/feedwater/ix/IXDSlice";
import OpenProjectStoreReducer from "@features/feedwater/modals/OpenProjectSlice";
import UFStoreReducer from "@features/feedwater/uf/UFSlice";
import CardListReducer from "@features/home/CardListSlice";
import FolderviewReducer from "@features/home/ViewAllFolderSlice";
import LicenseManagementReducer from "@features/LicenseManagement/store";
import AuthReducer from "@features/login/AuthSlice";
import LeftPanelReducer from "@features/menu/SideMenuSlice";
import ROConfigReducer from "@features/RO/store";
import WarrantyManagementReducer from "@features/WarrantyManagement/store";

// Sanitize large payloads to prevent memory issues
const actionSanitizer = action => {
  // Limit action payload size
  if (action.payload && typeof action.payload === "object") {
    const payloadStr = JSON.stringify(action.payload);
    if (payloadStr.length > 50000) {
      return {
        ...action,
        payload: "[PAYLOAD_TOO_LARGE - " + (payloadStr.length / 1024).toFixed(2) + " KB]",
      };
    }
  }
  return action;
};

const stateSanitizer = state =>
  // Return state as-is but with serialization limits handled by devTools
  state;

export const store = configureStore({
  reducer: {
    globalAppData: GlobalDataReducer,
    userInfo: UserInfoReducer,
    Auth: AuthReducer,
    cardlist: CardListReducer,
    Folderview: FolderviewReducer,
    leftpanel: LeftPanelReducer,
    waterCatalog: WaterTypesReducer,
    projectInfo: ProjectInfoReducer,
    systemDesign: SystemDesignSliceReducer,
    feedSetup: FeedSetupSlice,
    chemicalAdjustmentData: ChemicalAdjustmentReducer,
    IXStore: IXStoreReducer,
    ROConfig: ROConfigReducer,
    UFStore: UFStoreReducer,
    [postApi.reducerPath]: postApi.reducer,
    scrollData: activityMonitorSlice,
    apiData: ApiReducer,
    backgroundReport: BackgroundReportReducer,
    mldReport: MldReportReducer,
    licenseManagement: LicenseManagementReducer,
    warrantyManagement: WarrantyManagementReducer,
    OpenProjectStore: OpenProjectStoreReducer,
    GUnitConversion: GUnitConversionReducer,
  },
  middleware: getDefaultMiddleware => getDefaultMiddleware().concat(postApi.middleware),
  devTools: {
    trace: true,
    traceLimit: 12,
    maxAge: 500,
    actionSanitizer,
    stateSanitizer,
    predicate: (state, action) => {
      // Filter out noisy RTK Query and validation actions
      const actionType = action.type || "";
      return !(
        actionType.startsWith("@@rtkq/") ||
        actionType.startsWith("__rtkq/") ||
        actionType.includes("postApi/") ||
        actionType.includes("updateFeedValidationErrorById") ||
        actionType.includes("updateRODataStatus")
      );
    },
    features: {
      pause: true,
      lock: true,
      persist: true,
      export: true,
      jump: true,
      dispatch: true,
    },
  },
});
setupListeners(store.dispatch);
